import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Objection {
  id: string;
  tenant_id: string;
  category: string;
  trigger_keywords: string[];
  primary_response: string;
  alternative_responses: string[];
  strategy: string | null;
  is_active: boolean;
  usage_count: number;
  success_rate: number | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export function useObjectionLibrary() {
  return useQuery({
    queryKey: ["objection_library"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("objection_library")
        .select("*")
        .order("category", { ascending: true });

      if (error) throw error;
      return data as Objection[];
    },
  });
}

export function useActiveObjections() {
  return useQuery({
    queryKey: ["objection_library", "active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("objection_library")
        .select("*")
        .eq("is_active", true)
        .order("category", { ascending: true });

      if (error) throw error;
      return data as Objection[];
    },
  });
}

export function useCreateObjection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (objection: {
      category: string;
      trigger_keywords: string[];
      primary_response: string;
      alternative_responses?: string[];
      strategy?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("tenant_id")
        .eq("id", user.id)
        .maybeSingle();

      if (!profile?.tenant_id) {
        throw new Error("No tenant assigned");
      }

      const { data, error } = await supabase
        .from("objection_library")
        .insert({ 
          ...objection, 
          tenant_id: profile.tenant_id,
          alternative_responses: objection.alternative_responses || []
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["objection_library"] });
      toast.success("Objection handler added");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateObjection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, metadata, ...updates }: Partial<Objection> & { id: string }) => {
      const { data, error } = await supabase
        .from("objection_library")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["objection_library"] });
      toast.success("Objection handler updated");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteObjection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("objection_library")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["objection_library"] });
      toast.success("Objection handler deleted");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useMatchObjection() {
  return useMutation({
    mutationFn: async (text: string) => {
      const { data, error } = await supabase.functions.invoke("match-objection", {
        body: { text },
      });

      if (error) throw error;
      return data;
    },
    onError: (error) => {
      toast.error(`Failed to match objection: ${error.message}`);
    },
  });
}
